import { Box, Button, Chip, Typography, useMediaQuery, useTheme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { convertProductToInsertInput } from '@kheops/utils';
import { PackagingFamilyTypeIntl } from '../../../common/i18n/product-family.translation';
import { RoutePaths } from '../../../routes/AppRoutes';
import useOpenable from '../../../hooks/useOpenable';
import { ProductFragment } from '../../products';
import DeleteProductConfirmDialog from '../DeleteProductConfirmDialog';
import { commonSnackbarPropsAtom } from '../../../common/state/state';
import ProductUtils, { isNonFoodProduct } from '../../common/products.utils';
import { useDeleteProductMutation } from '../../../mutations/__generated__/deleteProduct.generated';
import { PackagingsByCompanyDocument } from '../../../queries/__generated__/packagingsByCompany.generated';
import BackButton from '../../../common/components/BackButton';
import { SubCatalogsByCompanyIdDocument } from '../../../queries/__generated__/subCatalogsByCompanyId.generated';
import { canEditProductAtom } from '../../../company/state';
import ProductNameOptimizationCard from '../../naming-wizard/ProductNameOptimizationCard';
import ProductLabels from './ProductLabels';
import ProductVatRate from './ProductVatRate';
import ProductIngredients from './ProductIngredients';
import ProductAllergens from './ProductAllergens';
import ProductNutritionFacts from './ProductNutritionFacts';
import ProductInfoDialog from './ProductInfoDialog';
import ProductDescription from './ProductDescription';
import ShelfLifeDurationDisplay from './ShelfLifeDurationDisplay';
import ProductLastModificationDates from './ProductLastModificationDates';
import ProductPreservationMethod from './ProductPreservationMethod';

interface ProductInfoProps {
  product: ProductFragment;
  isNameToOptimize?: boolean;
}

export default function ProductInfo({ product, isNameToOptimize }: ProductInfoProps): React.JSX.Element {
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'products', 'product-family', 'product-classification', 'product-classification-tags']);
  const [deleteProduct, deleteProductOutput] = useDeleteProductMutation({ refetchQueries: ['ProductVersionById', PackagingsByCompanyDocument, SubCatalogsByCompanyIdDocument] });
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const canEdit = useAtomValue(canEditProductAtom);
  const { open, close, isOpen } = useOpenable();
  const { open: openDeleteProductConfirm, close: closeDeleteProductConfirm, isOpen: isDeleteProductConfirmOpen } = useOpenable();

  const handleDeleteConfirm = useCallback((confirm: boolean): void => {
    if (confirm) {
      const productInsertInput = convertProductToInsertInput(product);

      productInsertInput.deleted_at = new Date().toISOString();

      deleteProduct({
        variables: {
          product: productInsertInput,
          sku: productInsertInput.sku!,
          company_id: productInsertInput.company_id!,
        },
        update: (cache) => {
          cache.evict({ fieldName: 'product' });
        },
      });
    } else {
      closeDeleteProductConfirm();
    }
  }, []);

  useEffect((): void => {
    if (deleteProductOutput.data?.insert_product) {
      setCommonSnackbarProps({
        label: t('common:update_successful'),
        snackbarProps: {
          open: true,
        },
      });

      closeDeleteProductConfirm();
      navigate(RoutePaths.COMPANY_PRODUCTS);
    }
  }, [deleteProductOutput]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            sm: 'row',
            lg: 'column',
            xl: 'row',
          },
          alignItems: 'start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
          <Box sx={{ display: 'flex', alignItems: 'top', gap: 1, mb: 1 }}>
            <BackButton
              to={RoutePaths.COMPANY_PRODUCTS}
              shouldCheckHistory={false}
              compact
              sx={{
                display: {
                  xs: 'none',
                  lg: 'inline-flex',
                },
              }}
            />
            <Typography variant="titleLarge" sx={{ alignSelf: 'center' }}>
              {product.name}
            </Typography>
          </Box>
          <Chip
            variant="tag"
            icon={<ShoppingBasketOutlinedIcon />}
            label={t(`product-family:${PackagingFamilyTypeIntl[product.sub_family as Product_Sub_Family_Name_Enum]}`)}
          />
        </Box>
        {canEdit && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'row',
                sm: 'column',
                lg: 'row',
                xl: 'column',
              },
              gap: 1,
              flexShrink: 0,
              mt: {
                xs: 1,
                sm: 0,
                lg: 1,
                xl: 0,
              },
            }}
          >
            <Chip
              variant="assistive"
              label={t('common:modify')}
              icon={<EditIcon />}
              component={Link}
              to={`${RoutePaths.PRODUCT_ADD_INFO}?productId=${product.id}`}
            />
            <Chip
              variant="assistive"
              label={t('common:delete')}
              icon={<DeleteOutlineIcon />}
              onClick={openDeleteProductConfirm}
            />
          </Box>
        )}
      </Box>
      {canEdit && isNameToOptimize && (
        <ProductNameOptimizationCard product={product} />
      )}
      {!!product.labels.length && (
        <ProductLabels labels={product.labels} short={isDownMd} />
      )}
      {
        !isDownMd
          ? (
            <>
              {product.description && <ProductDescription description={product.description} />}
              {product.generic_product_code && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="titleSmall">
                    {t('products:product_nature')}
                  </Typography>
                  <Typography variant="bodyMedium">
                    {t(`product-classification:${product.generic_product_code}`)}
                  </Typography>
                </Box>
              )}
              {!!product.classification_tags.length && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="titleSmall">
                    {t('products:classification_tags')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {product.classification_tags.map(({ tag_code }) => (
                      <Chip
                        key={tag_code}
                        variant="outlinedTag"
                        label={t(`product-classification-tags:${tag_code}`)}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {product.brand && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="titleSmall">
                    {t('products:brand')}
                  </Typography>
                  <Typography variant="bodyMedium">
                    {product.brand}
                  </Typography>
                </Box>
              )}
              {product.alcohol_by_volume && ProductUtils.isAlcoholProduct(product.sub_family as Product_Sub_Family_Name_Enum) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="titleSmall">
                    {t('products:alcohol_by_volume')}
                  </Typography>
                  <Typography variant="bodyMedium">
                    {product.alcohol_by_volume} {t('products:alcohol_by_volume_unit')}
                  </Typography>
                </Box>
              )}
              {!isNonFoodProduct(product.sub_family as Product_Sub_Family_Name_Enum) && (
                <ProductPreservationMethod preservationMethod={product.preservation_method} />
              )}
              <ProductVatRate
                vatRate={product.vat_rate!}
                sx={{
                  display: {
                    xs: 'none',
                    md: 'flex',
                  },
                }}
              />
              <ShelfLifeDurationDisplay
                titleVariant="titleSmall"
                duration={product.shelf_life_duration}
                durationType={product.shelf_life_duration_type}
                valueTypographyVariant="bodyMedium"
              />
              {(product.ingredients || product.is_exempt_from_ingredient_list) && (
                <ProductIngredients
                  ingredients={product.ingredients}
                  isExempt={product.is_exempt_from_ingredient_list}
                />
              )}
              {(product.allergens || product.has_no_allergen_declaration) && (
                <ProductAllergens
                  allergens={product.allergens}
                  hasDeclaration={product.has_no_allergen_declaration}
                />
              )}
              <ProductNutritionFacts product={product} />
              <ProductLastModificationDates
                titleVariant="titleSmall"
                last_price_updated_date={product.last_price_updated_date}
                last_other_updated_date={product.last_other_updated_date}
              />
            </>
          ) : (
            <Button
              variant="text"
              sx={{ alignSelf: 'start' }}
              disableRipple
              onClick={open}
            >
              + {t('products:see_product_details')}
            </Button>
          )
      }
      <ProductInfoDialog open={isOpen} onClose={close} product={product} />
      {isDeleteProductConfirmOpen && (
        <DeleteProductConfirmDialog open={isDeleteProductConfirmOpen} onClose={handleDeleteConfirm} product={product} />
      )}
    </Box>
  );
}
